import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { ngxCsv } from 'ngx-csv/ngx-csv';
import {
  QUALIFICATION_CODES,
  FILE_UPLOAD_LOCATION_QUOTAS,
} from '../../shared/constants/qualification-code';
import { V2SurveysQualification } from '../../shared/interfaces/qualification.interface';
import { AuthService } from '../../shared/services/auth/auth.service';
import { BuyerApiService } from '../../shared/services/buyer-api/survey.service';
import { SupplierApiService } from '../../shared/services/supplier-api/supplier-api.service';
import { Constants } from '../constants/manage-audience.constants';
import { ChildrenLabelMapper } from 'purespectrum-lib';

@Component({
  selector: 'ps-ui-manage-audience',
  templateUrl: './manage-audience.component.html',
  styleUrls: ['./manage-audience.component.css'],
})
export class ManageAudienceComponent implements OnChanges {
  public audienceData: V2SurveysQualification[] = [];
  @Input() surveyId: number = 0;
  @Input() countryCode: string;
  readonly QUALIFICATION_CODES = QUALIFICATION_CODES;
  readonly CSV_QUALIFICATION_CODES: { [key: number]: boolean } = {};
  constructor(
    private _buyerApiService: BuyerApiService,
    private _supplierApiService: SupplierApiService,
    private _toastr: ToasterService,
    private _authService: AuthService
  ) {
    FILE_UPLOAD_LOCATION_QUOTAS.forEach((qCode: number) => {
      this.CSV_QUALIFICATION_CODES[qCode] = true;
    });
  }

  ngOnChanges(changes: SimpleChanges = {}): void {
    if (changes.surveyId.currentValue) {
      this._getQualifications(changes.surveyId.currentValue);
    }
  }

  private _getQualifications(surveyId: number) {
    if (this._authService.userType === 'supplier') {
      this._supplierApiService.getQualifications(surveyId).subscribe(
        (qualifications: V2SurveysQualification[]) => {
          qualifications = this._mapChildrenLabel(qualifications);
          this.audienceData = qualifications;
        },
        (err) => {
          this._toastr.error(err.error.msg);
        }
      );
    } else {
      this._buyerApiService.getQualifications(surveyId).subscribe(
        (qualifications: V2SurveysQualification[]) => {
          qualifications = this._mapChildrenLabel(qualifications);
          if (this._canShowExtraCoreQualifications()) {
            this.audienceData = qualifications;
            return;
          }
          this.audienceData = qualifications.filter(
            (qualification) =>
              qualification.qualification_code !==
              QUALIFICATION_CODES.RELATIONSHIP
          );
        },
        (err) => {
          this._toastr.error(err.error.msg);
        }
      );
    }
  }

  downloadCsv(
    audienceData: V2SurveysQualification,
    surveyId: number | undefined
  ) {
    const options: CSVOptions = {
      headers: [],
    };
    const qualificationCode = audienceData.qualification_code;
    let csvData: CSVData[] = audienceData.data;
    let csvName = '';
    if (qualificationCode === QUALIFICATION_CODES.ZIPCODE) {
      options.headers = ['zipcodes'];
      csvName = `zipcodes_${surveyId}`;
    } else if (qualificationCode === QUALIFICATION_CODES.FSA) {
      options.headers = [audienceData.key];
      csvName = this._returnCsvNameFromQualKey(audienceData.key, surveyId);
      csvData = csvData.map((fsa) => ({ name: fsa.name }));
    }
    this._createAndDownloadCsv(csvData, csvName, options);
  }

  private _returnCsvNameFromQualKey(
    qualificationKey: string,
    surveyId: number | undefined
  ) {
    return `${qualificationKey.split(' ').join('_')}_${surveyId}`;
  }

  private _createAndDownloadCsv(
    data: CSVData[],
    csvName: string,
    options: CSVOptions
  ) {
    return new ngxCsv(data, csvName, options);
  }

  _canShowExtraCoreQualifications() {
    return (
      this.countryCode !== Constants.AUSTRALIA_SHORT_CODE ||
      this._authService.buyerConfig?.enableExtraCoreQualification
    );
  }

  private _mapChildrenLabel(qualifications: V2SurveysQualification[]) {
    return qualifications.map((qualification) => {
      if (
        qualification.qualification_code == QUALIFICATION_CODES.CHILD_AGE_GENDER
      ) {
        if (
          qualification.childAgeGenderQuota &&
          qualification.childAgeGenderQuota.length
        ) {
          qualification.label = Array.from(
            new Set(
              qualification.childAgeGenderQuota.reduce<string[]>(
                (acc, quota) => {
                  const mapper = new ChildrenLabelMapper(
                    quota.condition_codes!.map(Number),
                    quota.child_age_unit
                  );
                  acc.push(mapper.label());
                  return acc;
                },
                []
              )
            )
          ).join('; ');
        } else {
          const childAgeUnit = qualification.childAgeUnit;
          const mapper = new ChildrenLabelMapper(
            qualification.condition_codes!.map(Number),
            childAgeUnit
          );
          qualification.label = mapper.label();
        }
      }
      return qualification;
    });
  }
}

interface CSVOptions {
  headers: string[];
}

interface CSVData {
  [key: string]: string;
}
